// =============================================================================
// hestia/mutuelles.js : la mutuelle de chaque fiche, son DG, l'équipe d'Enio.
// -----------------------------------------------------------------------------
// Décision du DG d'Enio du 4 octobre 2026, après-midi : « On n'a pas besoin
// des places : dans les fiches membres, chaque membre a sa mutuelle
// indiquée. [...] Les membres d'une mutuelle sont automatiquement soumis à
// l'approbation de la personne marquée DG au sein de sa mutuelle indiquée. »
// La mutuelle d'une personne est le champ `mutuelle` de sa fiche, comparé
// sans casse ni accents (cleMutuelle). UMGP : ses fiches portent la mutuelle
// « UMGP », leur DG est le DG de l'UMGP ; `mutuelleGouvernance` (MEP ou
// SMEREP) ne compte pas ici, il reste celui des sièges de gouvernance.
// Le DG d'une mutuelle : toute fiche éligible qui porte le rôle DG et la même
// mutuelle (dgsDeMutuelle), RELUE à chaque appel, jamais figée sur un acte ;
// aucune désignation, aucune configuration. L'équipe d'Enio (mutuelle
// « Enio ») a pour DG le DG d'Enio, identité de la configuration.
// La mutuelle est FIGÉE sur chaque demande au moment de la demande
// (circuit.js). Jamais un nom ni un identifiant écrit dans le code.
// Une configuration de « places » écrite avant cette décision (aucune en
// production) est ignorée : ni lue, ni projetée (lib/hestiaServeur.js).
// Module pur.
// =============================================================================

import { trierParLibelle, trierPersonnes } from "./ordre";
// Qui est administrateur : lib/administrateurs.js au serveur (substitution de
// scripts/sync-hestia-serveur.js), son miroir src/retraitRoleAdmin.js ici.
import { estFicheAdministrateur, estFicheSuperAdministrateur } from "../retraitRoleAdmin";

export const EQUIPE_ENIO = "enio";

const norm = (s) => String(s == null ? "" : s).normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/\s+/g, " ").trim();
const roles = (f) => (Array.isArray(f && f.roles) ? f.roles : []);
const libellePropre = (s) => String(s == null ? "" : s).replace(/\s+/g, " ").trim();

/** La clé d'un nom de mutuelle : sans casse, sans accents, espaces réduits
 *  (« Intériale », « interiale » et « INTÉRIALE » sont la même mutuelle). */
export const cleMutuelle = (s) => norm(s);
/** Le nom d'une mutuelle tel qu'on l'affiche (espaces réduits). */
export const libelleMutuelle = (s) => libellePropre(s);

/** Fiche qui peut porter un droit dans Hestia : active, non supprimée, ni
 *  compte technique ni compte invité (les comptes techniques sont exclus même
 *  avec le rôle d'administrateur, src/hestia/habilitation.js). */
export function ficheEligible(f) {
  return !!(f && f.id && f.actif !== false && !f.supprime && !f.technique
    && f.role !== "invite" && f.id !== "argos-verif");
}

/** Les champs d'AUTORITÉ des fiches (`role`, `roles`, mandats, `mutuelle`,
 *  `assistantes`) sont-ils protégés en écriture ? Depuis FB10a (règle du
 *  30/09/2026, firestore.rules), seul un administrateur authentifié les
 *  change. `autoriteProtegee` de la configuration est la DÉCLARATION, posée
 *  par un administrateur, que cette règle est relue en production (plan V3,
 *  § 5) : Hestia ne s'y fie pas avant. Échec fermé : tant que ce n'est pas
 *  posé, seul le super-administrateur entre, comme pour hestiaAdmin. */
export const autoriteFiable = (ctx) => !!(ctx && ctx.config && ctx.config.autoriteProtegee === true);

/** Super-administrateur d'Aramis (rôle de la fiche relue). */
export const estSuperAdministrateur = (f) => ficheEligible(f) && estFicheSuperAdministrateur(f);

/** Administrateur d'Aramis (rôle de la fiche relue, jamais de la session).
 *  `ctx` (l'état d'Hestia) est OBLIGATOIRE : sans lui, ou tant que l'autorité
 *  n'est pas protégée, seul le super-administrateur compte (échec fermé). */
export const estAdministrateur = (f, ctx) => ficheEligible(f)
  && (estFicheSuperAdministrateur(f) || (estFicheAdministrateur(f) && autoriteFiable(ctx)));

/** Administrateur (pas super-administrateur) que la porte retient encore :
 *  l'autorité des fiches n'est pas déclarée protégée (vues.js le lui dit). */
export const administrateurRetenu = (f, ctx) => ficheEligible(f)
  && estFicheAdministrateur(f) && !estFicheSuperAdministrateur(f) && !autoriteFiable(ctx);

export const estEquipeEnio = (f) => norm(f && f.mutuelle) === EQUIPE_ENIO;

/** La clé de la mutuelle d'une fiche : EQUIPE_ENIO pour l'équipe d'Enio, ""
 *  pour une fiche sans mutuelle. */
export const mutuelleDe = (f) => norm(f && f.mutuelle);

/** La fiche porte-t-elle le rôle DG d'une mutuelle (hors équipe d'Enio) ?
 *  Éligible, rôle DG, mutuelle renseignée. Ce que lisent l'habilitation par
 *  fonction (habilitation.js) et le rôle DG de la matrice (matrice.js). */
export const estDGDeMutuelle = (f) => ficheEligible(f) && roles(f).includes("DG")
  && !!mutuelleDe(f) && mutuelleDe(f) !== EQUIPE_ENIO;

/** Les DG d'une mutuelle (clé), RELUS à chaque appel : les fiches éligibles
 *  qui portent le rôle DG et cette mutuelle, en ordre alphabétique. Aucun
 *  pour l'équipe d'Enio (son DG est le DG d'Enio, dgEnioActif). */
export function dgsDeMutuelle(cle, fiches) {
  const k = norm(cle);
  if (!k || k === EQUIPE_ENIO) return [];
  return trierPersonnes((Array.isArray(fiches) ? fiches : []).filter((f) => estDGDeMutuelle(f) && mutuelleDe(f) === k));
}

/** Le DG d'Enio de la configuration, si sa fiche est éligible ; sinon null. */
export function dgEnioActif(fiches, config) {
  const id = config && config.identites && config.identites.dgEnio;
  return id && (Array.isArray(fiches) ? fiches : []).some((f) => f && f.id === id && ficheEligible(f)) ? id : null;
}

/** Les identifiants de tous les DG de mutuelle (hors équipe d'Enio) : ceux
 *  dont le serveur lit l'accès à Aramis (functions/hestia.js, lireAcces). */
export const tousLesDGDeMutuelle = (fiches) => (Array.isArray(fiches) ? fiches : []).filter(estDGDeMutuelle).map((f) => f.id);

/** Les mutuelles que portent les fiches éligibles, hors équipe d'Enio et
 *  hors fiche sans mutuelle : [{ cle, libelle, fiches }], en ordre
 *  alphabétique. Le libellé est l'écriture la plus fréquente parmi les
 *  fiches (départage alphabétique). */
export function mutuellesDesFiches(fiches) {
  const parCle = new Map();
  for (const f of (Array.isArray(fiches) ? fiches : []).filter(ficheEligible)) {
    const cle = mutuelleDe(f);
    if (!cle || cle === EQUIPE_ENIO) continue;
    const x = parCle.get(cle) || { cle, fiches: 0, ecritures: new Map() };
    const nom = libellePropre(f.mutuelle);
    x.fiches += 1;
    x.ecritures.set(nom, (x.ecritures.get(nom) || 0) + 1);
    parCle.set(cle, x);
  }
  const libelleDe = (ecritures) => trierParLibelle([...ecritures.entries()].map(([libelle, n]) => ({ libelle, n })))
    .reduce((a, b) => (b.n > a.n ? b : a)).libelle;
  return trierParLibelle([...parCle.values()].map((x) => ({ cle: x.cle, libelle: libelleDe(x.ecritures), fiches: x.fiches })));
}

/** Le libellé d'une mutuelle (clé) parmi les fiches, ou `repli`. */
export function libelleDeMutuelle(cle, fiches, repli = "") {
  const k = norm(cle);
  if (k === EQUIPE_ENIO) return "Équipe d'Enio";
  const m = mutuellesDesFiches(fiches).find((x) => x.cle === k);
  return m ? m.libelle : libellePropre(repli);
}

/** Ce que le retrait d'une fiche d'Hestia ferait au premier temps (relecture
 *  H1, tour 3) : { equipe: true } pour le DG d'Enio ; { mutuelle, seul } pour
 *  un DG de mutuelle (seul : aucun autre DG de sa mutuelle ne validerait à sa
 *  place) ; null sinon. */
export function premierTempsTenuPar(f, fiches, config) {
  if (!f || !f.id) return null;
  if (estEquipeEnio(f)) return dgEnioActif(fiches, config) === f.id ? { equipe: true } : null;
  if (!estDGDeMutuelle(f)) return null;
  const dgs = dgsDeMutuelle(mutuelleDe(f), fiches);
  return { mutuelle: libelleDeMutuelle(mutuelleDe(f), fiches, f.mutuelle), seul: dgs.length === 1 };
}

// ── Écriture de la configuration (lot H1, hestiaAdmin) ──────────────────────
/** Délais de G22 (relecture sous 48 heures) et G23 (réponse sous 60 jours). */
export const DELAIS = Object.freeze({ relectureHeures: 48, reponseJours: 60 });

/** La configuration d'avant toute écriture : aucune identité, aucun drapeau
 *  posé (échec fermé : seul le super-administrateur entre). */
export const configNeuve = () => ({
  identites: { dgEnio: null, president: null, dops: null },
  autoriteProtegee: false, orgEnioProtege: false, orgEnioVivant: false, ouverte: false,
  delais: { ...DELAIS }, boitesTest: [], traces: {}, version: 0,
});

/** Boîtes de test (essais du courriel au DG, puis de la Lettre en H3) : les
 *  SEULES adresses, avec celle de l'administrateur qui essaie, vers
 *  lesquelles un essai peut partir. Cinq au plus. */
export const BOITES_TEST_MAX = 5;
export const adresseValide = (s) => typeof s === "string" && s.length <= 254 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
export function normaliserBoitesTest(liste) {
  if (!Array.isArray(liste)) return { erreur: "Les boîtes de test forment une liste d'adresses." };
  const out = [...new Set(liste.map((x) => String(x == null ? "" : x).trim().toLowerCase()).filter(Boolean))];
  if (out.length > BOITES_TEST_MAX) return { erreur: `${BOITES_TEST_MAX} boîtes de test au plus.` };
  if (!out.every(adresseValide)) return { erreur: "Une boîte de test n'est pas une adresse e-mail valide." };
  return { boitesTest: out };
}

/** Problèmes de configuration à montrer en Pilotage (aucun ne s'efface seul,
 *  aucun n'empêche l'ouverture). Une mutuelle sans DG n'en est pas un : ses
 *  demandes vont à un administrateur seul (circuit.js), Pilotage le montre
 *  dans la liste des mutuelles. */
export function problemesConfig(config, fiches) {
  if (!config) return ["La configuration d'Hestia n'est pas encore écrite : seuls les super-administrateurs entrent."];
  const out = [];
  if (config.autoriteProtegee !== true) {
    out.push("Les droits des fiches (rôles, mandats, mutuelle, assistantes) ne sont pas encore protégés en écriture : seuls les super-administrateurs entrent.");
  }
  // Sans adresse, un DG ne reçoit jamais le courriel qui lui dit qu'une
  // demande l'attend (relecture H1) : à compléter sur sa fiche.
  for (const m of mutuellesDesFiches(fiches)) {
    const dgs = dgsDeMutuelle(m.cle, fiches);
    const sans = dgs.filter((f) => !adresseValide(String(f.email || "").trim()));
    if (!sans.length) continue;
    out.push(dgs.length === 1
      ? `« ${m.libelle} » : la fiche du DG n'a pas d'adresse e-mail valide ; il ne recevra aucun courriel d'Hestia.`
      : `« ${m.libelle} » : ${sans.length === 1 ? "la fiche d'un de ses DG n'a" : "des fiches de ses DG n'ont"} pas d'adresse e-mail valide ; ${sans.length === 1 ? "il ne recevra" : "ils ne recevront"} aucun courriel d'Hestia.`);
  }
  const id = config.identites || {};
  if (!id.dgEnio) out.push("Le DG d'Enio n'est pas renseigné.");
  else {
    const f = (fiches || []).find((x) => x && x.id === id.dgEnio);
    if (f && !adresseValide(String(f.email || "").trim())) out.push("La fiche du DG d'Enio n'a pas d'adresse e-mail valide ; il ne recevra aucun courriel d'Hestia.");
  }
  if (!id.president) out.push("Le Président d'Enio n'est pas renseigné.");
  return out;
}
